/**
 * Pfad: src/utils/debuglog.js
 * Zweck/Funktion:
 * - Einheitliches Debug-/System-Logging für Backend (Node/Express) und Frontend (React).
 * - Robust gegen verschiedene Import-Stile (CommonJS require / ESM import).
 * - Farb- und Level-Helfer (rot/gelb/gruen/blau/grau sowie debug/info/warn/error).
 * - 30-Minuten-"Burst-Logging": in einem Zeitraum ALLE Meldungen (nicht nur Warn/Error).
 * - Backend: optionales File-Logging nach APP_ROOT_DIR/.run/debug.log (Standard: /var/www/experts4europe/.run/debug.log).
 *
 * --------------------------------------------------------------------------------------
 * BACKEND (Node/Express) – Beispiele
 * --------------------------------------------------------------------------------------
 *  // 1) Import (CommonJS)
 *  const { debugLog, setDebugMode, clearDebugMode, getDebugStatus } = require("../utils/debuglog");
 *
 *  // 2) Grundnutzung
 *  debugLog("system", "Server startet");
 *  debugLog.grau("[#abcd] ➡️ GET /api/ping ip=1.2.3.4");
 *  debugLog.gruen("[#abcd] ⬅️ GET /api/ping status=200 took=8.3ms", "DE000100A");
 *  debugLog.gelb("Konfiguration geladen (ohne Secrets)");      // Warnhinweis / gelb
 *  debugLog.rot("DB-Fehler beim Insert: " + err.message);      // Fehler / rot
 *
 *  // 3) 30-Minuten Burst-Logging aktivieren (alle Meldungen durchlassen)
 *  setDebugMode(30 * 60 * 1000); // 30 Minuten
 *  // Status abfragen:
 *  const st = getDebugStatus(); // { enabled, untilISO, remainingMs }
 *
 *  // 4) gezielt wieder deaktivieren
 *  clearDebugMode();
 *
 * --------------------------------------------------------------------------------------
 * FRONTEND (React) – Beispiele
 * --------------------------------------------------------------------------------------
 *  // 1) Import (ESM)
 *  import { debugLog, setDebugMode, clearDebugMode, getDebugStatus } from "../utils/debuglog";
 *
 *  // 2) Nutzung in einer Page/Komponente:
 *  debugLog("ui", "ApplicantCV2 geöffnet");
 *  debugLog.gelb("[CV2] Vollständigkeit gering – Nutzerhinweis eingeblendet");
 *  debugLog.rot("[CV2] Speichern fehlgeschlagen: " + (err?.message || "Unbekannt"));
 *
 *  // 3) Optional: kurzfristig mehr Details
 *  setDebugMode(5 * 60 * 1000); // 5 Minuten
 *  console.log(getDebugStatus());
 *
 *  // Hinweis:
 *  // Im Frontend wird NIE in eine Datei geschrieben – nur die Konsole (Browser DevTools).
 *
 * --------------------------------------------------------------------------------------
 * ENV‑Schalter (nur Backend / Node):
 * --------------------------------------------------------------------------------------
 *  DEBUG_BURST_MINUTES    = Zahl (Standard: 30)
 *  DEBUG_BURST_ON_START   = "1" | "0"  (Standard: "1")  → beim ersten require() Burst sofort aktiv
 *  DEBUG_LOG_FILE         = Pfad (Standard: "/var/www/experts4europe/.run/debug.log")
 *  DEBUG_TO_FILE          = "1" | "0"  (Standard: "1")  → File-Logging ein/aus
 *
 * Format:
 *  "DD.MM.YYYY, HH:MM:SS, <channel>, <message>"   // z. B. "31.8.2025, 17:10:00, system, Server startet"
 */


(function UMD(factory){
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else if (typeof define === 'function' && define.amd) define([], factory);
  else { const api = factory(); try { if (typeof window !== 'undefined') window.debuglog = api; } catch(_) {} }
})(function(){
  const isNode = typeof process !== 'undefined' && process.release && process.release.name === 'node';

  // ANSI-Farben nur in Node
  const COLORS = isNode
    ? { reset:'\x1b[0m', gray:'\x1b[90m', blue:'\x1b[34m', green:'\x1b[32m', yellow:'\x1b[33m', red:'\x1b[31m' }
    : { reset:'',        gray:'',        blue:'',        green:'',        yellow:'',        red:'' };

  // Datei-Logging (optional)
  const APP_ROOT_DIR = isNode ? (process.env.APP_ROOT_DIR || '/var/www/experts4europe') : null;
  const LOG_FILE = isNode ? (process.env.DEBUG_LOG_FILE || `${APP_ROOT_DIR}/.run/debug.log`) : null;
  const TO_FILE  = isNode ? ((process.env.DEBUG_TO_FILE ?? '1') === '1') : false;

  // Dedupe (ms)
  const DEDUPE_MS = isNode ? Number(process.env.DEBUG_DEDUPE_WINDOW_MS || 1500) : 1500;
  const recent = new Map();

  // Farbwörter (nur zur *Erkennung* von Alt-Aufrufen / Drop)
  const LEGACY_COLOR = new Set(['rot','gelb','gruen','blau','grau','red','yellow','green','blue','gray','grey']);
  const COLOR_MAP = { rot:'red', red:'red', gelb:'yellow', yellow:'yellow', gruen:'green', green:'green', blau:'blue', blue:'blue', grau:'gray', gray:'gray', grey:'gray' };
  const SEV_FOR = (c) => (c==='red' ? 'error' : c==='yellow' ? 'warn' : 'info');

  // Utils
  const stamp = () => new Date().toLocaleString('de-DE',{year:'numeric',month:'numeric',day:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false});
  const pad   = (ch) => ch ? String(ch) : 'system';
  function firstTokenAsChannel(s){
    if (!s || typeof s!=='string') return { channel:'system', message:'' };
    const i = s.indexOf(' ');
    if (i<=0) return { channel:'system', message:s };
    const tok = s.slice(0,i).trim();
    if (/^[a-z0-9._\-:]{2,32}$/i.test(tok)) return { channel:tok, message:s.slice(i+1).trim() };
    return { channel:'system', message:s };
  }
  function toLine(ch,msg,uid){ const parts=[stamp(), pad(ch), msg]; if (uid) parts.push(String(uid)); return parts.join(', '); }
  function write(line){
    if(!isNode || !TO_FILE || !LOG_FILE) return;
    try{
      require('fs').appendFile(LOG_FILE, line+'\n', (error)=>{
        if (!error || global.__E4E_DEBUGLOG_WRITE_REPORTED__) return;
        global.__E4E_DEBUGLOG_WRITE_REPORTED__ = true;
        console.error(COLORS.red + `${stamp()}, debuglog, File-Logging fehlgeschlagen (${LOG_FILE}): ${error.message}` + COLORS.reset);
      });
    }catch(error){
      if (global.__E4E_DEBUGLOG_WRITE_REPORTED__) return;
      global.__E4E_DEBUGLOG_WRITE_REPORTED__ = true;
      console.error(COLORS.red + `${stamp()}, debuglog, File-Logging konnte nicht initialisiert werden (${LOG_FILE}): ${error.message}` + COLORS.reset);
    }
  }
  function dedupeSig(ch,msg){ return `${(ch||'').trim()}|${(msg||'').trim()}`; }
  function seen(ch,msg){
    if(!DEDUPE_MS) return false;
    const now=Date.now(), key=dedupeSig(ch,msg), last=recent.get(key);
    if(last && now-last<DEDUPE_MS) return true;
    recent.set(key, now);
    if (recent.size>2000){ for(const [k,t] of recent){ if(now-t>DEDUPE_MS) recent.delete(k); } }
    return false;
  }

  // Kern-Ausgabe
  function emit({severity,color,channel,message,userId}){
    if (seen(channel,message)) return;                         // Doppelzeile wegschnappen
    const line = toLine(channel,message,userId);
    if (isNode){
      const c = color==='red'?COLORS.red: color==='yellow'?COLORS.yellow: color==='green'?COLORS.green: color==='blue'?COLORS.blue: color==='gray'?COLORS.gray: COLORS.reset;
      const out = severity==='error'? console.error : severity==='warn'? console.warn : console.log;
      out(c + line + COLORS.reset);
      write(line);
    } else {
      const out = severity==='error'? console.error : severity==='warn'? console.warn : console.log;
      out(line);
    }
  }

  // Strikte Normalisierung: **nur** neue API
  //  - debugLog(CHANNEL, MESSAGE[, userId])
  //  - debugLog(MESSAGE)     → channel = system
  //  - debugLog({channel, message, userId})
  //  - Falls CHANNEL ein Farbworte (Alt): Farbe/Level ableiten, Channel aus MESSAGE-Token ziehen.
  function normalize(args, defaultColor, defaultSeverity){
    let color = defaultColor || 'gray';
    let severity = defaultSeverity || 'info';
    let channel = 'system', message = '', userId;

    if (args.length===1 && args[0] && typeof args[0]==='object'){
      const a=args[0]; return { color, severity, channel:a.channel||'system', message:a.message||'', userId:a.userId };
    }

    const A=args[0], B=args[1], C=args[2];

    if (typeof A==='string' && typeof B==='string'){ channel=A; message=B; userId=C; }
    else if (typeof A==='string'){ message=A; }
    else { message = Array.from(args).map(x=> typeof x==='string'? x : JSON.stringify(x)).join(' '); }

    // Alt: CHANNEL ist Farbworte → in neue Form zwingen (kein Alt-Output!)
    if (LEGACY_COLOR.has((channel||'').toLowerCase())) {
      const mapped = COLOR_MAP[(channel||'').toLowerCase()];
      color = mapped || color;
      severity = SEV_FOR(color);
      const s = firstTokenAsChannel(message);
      channel = s.channel;
      message = s.message;
    }
    return { color, severity, channel, message, userId };
  }

  // Öffentliche API
  function make(defaultColor, defaultSeverity){
    return function(){ emit( normalize(arguments, defaultColor, defaultSeverity) ); };
  }

  const debugLog = make('gray','info');
  debugLog.grau  = make('gray','info');
  debugLog.blau  = make('blue','info');
  debugLog.gruen = make('green','info');
  debugLog.gelb  = make('yellow','warn');
  debugLog.rot   = make('red','error');
  debugLog.debug = make('gray','debug');
  debugLog.info  = make('blue','info');
  debugLog.warn  = make('yellow','warn');
  debugLog.error = make('red','error');

  // UI‑Kompatibilitäts‑Stubs (AdminDashboard nutzt das)
  let debugModeUntil = 0;
  function setDebugMode(ms){ debugModeUntil = Date.now()+Math.max(0,Number(ms||0)); return getDebugStatus(); }
  function clearDebugMode(){ debugModeUntil = 0; return getDebugStatus(); }
  function getDebugStatus(){ const remain=Math.max(0,debugModeUntil-Date.now()); return { enabled: remain>0, untilISO: remain>0? new Date(debugModeUntil).toISOString(): null, remainingMs: remain }; }

  // Kleiner Konsolen-Filter: *alle* Alt‑CSV‑Zeilen verwerfen (egal woher)
  if (isNode && !global.__E4G_DROP_OLD_CSV__) {
    global.__E4G_DROP_OLD_CSV__ = true;
    const reOld = /^\s*\d{1,2}\.\d{1,2}\.\d{4},\s*\d{1,2}:\d{2}:\d{2},\s*(rot|gelb|gruen|blau|grau|red|yellow|green|blue|gray|grey)\s*,/i;
    const wrap = (orig) => function wrapped(...args){
      try {
        const s = (args && typeof args[0]==='string') ? args[0] : null;
        if (s && reOld.test(s)) return; // Alt-Zeile **nicht** ausgeben
      } catch {}
      return orig.apply(console, args);
    };
    console.log  = wrap(console.log.bind(console));
    console.warn = wrap(console.warn.bind(console));
    console.error= wrap(console.error.bind(console));
  }

  return { debugLog, setDebugMode, clearDebugMode, getDebugStatus, default: debugLog };
});
